const { createApp, ref, computed, onMounted, onUnmounted, watch, reactive } = Vue;

/**
 * Vueアプリケーションを初期化または再初期化する関数
 */
const initVueApp = () => {
  const appElement = document.getElementById('app');
  const dataElement = document.getElementById('search-data');

  // 必要な要素が見つからない場合は処理を中断
  if (!appElement || !dataElement) {
    return;
  }

  // 既にVueインスタンスがマウントされている場合は、重複して初期化しない
  if (appElement.__vue_app__) {
    return;
  }

  const app = createApp({
    setup() {
      const data = reactive({
        isOpenFiltersSidebar: false,
      });

      const appData = JSON.parse(dataElement.textContent);

      // --- Helper Functions ---
      const normalize = (str) => {
        if (!str) return '';
        return str
          .normalize('NFKC')
          .toLowerCase()
          .replace(/[\u30a1-\u30f6]/g, (match) => String.fromCharCode(match.charCodeAt(0) - 0x60));
      };

      const extractTextFromRichText = (node) => {
        if (!node) return '';
        if (typeof node.value === 'string') return node.value;
        if (Array.isArray(node.children)) {
          return node.children.map(extractTextFromRichText).join(' ');
        }
        return '';
      };

      // --- URL Params & Initial State Setup ---
      const urlParams = new URLSearchParams(window.location.search);
      const qParam = ref(urlParams.get('q') || appElement.dataset.q || '');
      const typeParam = urlParams.get('type');
      const initialTab = typeParam === 'all' ? 'all' : 'product';
      const activeTab = ref(initialTab);

      const initializeFiltersFromURL = () => {
        const initialFilters = {
          service: [],
          product_family: [],
          category: [],
          usage: [],
        };

        const filterKeys = ['service', 'product_family', 'category', 'usage'];

        filterKeys.forEach(key => {
          const handles = urlParams.get(key);
          if (handles && appData.metaobjects[key]) {
            const handleArray = handles.split(',').map(h => h.trim());
            handleArray.forEach(handle => {
              const foundObject = appData.metaobjects[key].find(item => item.handle && item.handle.trim() === handle);
              if (foundObject) {
                initialFilters[key].push(foundObject.name);
              }
            });
          }
        });

        return initialFilters;
      };

      // --- Product Search State ---
      const allProducts = ref(appData.products);
      const metaobjectFilters = ref(appData.metaobjects);

      // Liquidのpaginateタグは1ページ最大250件までしか返せないため、
      // 商品が250件を超える場合は2ページ目以降を追加取得して結合する
      const totalSearchDataPages = appData.totalPages || 1;
      if (totalSearchDataPages > 1) {
        const basePath = window.location.pathname;
        const additionalPagePromises = [];
        for (let page = 2; page <= totalSearchDataPages; page++) {
          additionalPagePromises.push(
            fetch(`${basePath}?page=${page}&section_id=search-customed`)
              .then((response) => response.text())
              .then((html) => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const dataElement = doc.getElementById('search-data');
                if (!dataElement) return [];
                return JSON.parse(dataElement.textContent).products || [];
              })
              .catch((error) => {
                console.error(`search-data の追加ページ(${page})の取得に失敗しました:`, error);
                return [];
              })
          );
        }
        Promise.all(additionalPagePromises).then((pagesOfProducts) => {
          allProducts.value = [...allProducts.value, ...pagesOfProducts.flat()];
        });
      }
      const selectedFilters = ref(initializeFiltersFromURL());
      const productsKey = ref(0);
      const inStockOnly = ref(false);
      const sortOrder = ref('shopify_default');
      const currentPage = ref(1);
      const perPage = ref(15); // per page product

      // --- All Search (Shopify) State ---
      const searchTerms = ref(appElement.dataset.searchTerms || '');
      const searchUrl = ref(appElement.dataset.searchUrl || '/search');
      const searchResults = ref('');
      const isLoading = ref(false);
      const allSearchResultsCount = ref(null);
      const faqData = ref([]);
      const isFaqHit = ref(false);
      const allCurrentPage = ref(1);
      const allPerPage = ref(14); // per page all

      // --- All Search Logic ---
      const allPaginatedResults = computed(() => {
        if (!searchResults.value) return '';
        const parser = new DOMParser();
        const doc = parser.parseFromString(searchResults.value, 'text/html');
        const items = Array.from(doc.querySelectorAll('.search-result-item'));
        const start = (allCurrentPage.value - 1) * allPerPage.value;
        const end = start + allPerPage.value;
        const paginatedItems = items.slice(start, end);
        return paginatedItems.map(item => item.outerHTML).join('');
      });

      const allTotalPages = computed(() => {
        if (!searchResults.value) return 0;
        const parser = new DOMParser();
        const doc = parser.parseFromString(searchResults.value, 'text/html');
        const items = doc.querySelectorAll('.search-result-item');
        return Math.ceil(items.length / allPerPage.value);
      });

      const changeAllPage = (page) => {
        if (page >= 1 && page <= allTotalPages.value) {
          allCurrentPage.value = page;
          const appElement = document.getElementById('app');
          if (appElement) {
            document.documentElement.style.scrollBehavior = 'auto';
            appElement.scrollIntoView({ behavior: 'auto' });
            setTimeout(() => {
              document.documentElement.style.scrollBehavior = '';
            }, 100);
          }
        }
      };

      const performSearch = async () => {
        if (!searchTerms.value.trim()) {
          searchResults.value = '';
          allSearchResultsCount.value = 0;
          return;
        }
        isLoading.value = true;
        searchResults.value = '';
        allSearchResultsCount.value = null;
        isFaqHit.value = false;
        try {
          const response = await fetch(`${searchUrl.value}?q=${encodeURIComponent(searchTerms.value)}&section_id=ajax-search-all`);
          if (!response.ok) throw new Error('Network response was not ok');
          const responseHtml = await response.text();

          // DOMParserを使ってHTML文字列から件数を抽出
          const parser = new DOMParser();
          const doc = parser.parseFromString(responseHtml, 'text/html');
          const countElement = doc.getElementById('all-search-results-count');
          
          let count = countElement ? parseInt(countElement.textContent, 10) : 0;

          // FAQ Search
          const searchTerm = normalize(searchTerms.value);
          if (faqData.value && faqData.value.length > 0) {
            for (const item of faqData.value) {
              const question = item.question ? normalize(item.question) : '';
              const answer = item.answer ? normalize(extractTextFromRichText(item.answer)) : '';
              if (question.includes(searchTerm) || answer.includes(searchTerm)) {
                isFaqHit.value = true;
                count++;
                break;
              }
            }
          }
          
          allSearchResultsCount.value = count;

          const searchResultsList = doc.querySelector('.search-results-list');

          if (isFaqHit.value) {
            const faqHitHtml = `
              <div class="search-result-item">
                <a href="/pages/faq" class="search-result-item__link">
                  <div class="search-result-item__content">
                    <h3 class="search-result-item__title">よくあるご質問</h3>
                  </div>
                </a>
              </div>
            `;
            if (searchResultsList) {
              searchResultsList.insertAdjacentHTML('afterbegin', faqHitHtml);
              searchResults.value = searchResultsList.outerHTML;
            } else {
               searchResults.value = faqHitHtml;
            }
          } else {
            if (searchResultsList) {
              searchResults.value = searchResultsList.outerHTML;
            } else {
              searchResults.value = '';
            }
          }

        } catch (error) {
          console.error('There was a problem with the fetch operation:', error);
          searchResults.value = '<p>検索結果の取得中にエラーが発生しました。</p>';
          allSearchResultsCount.value = isFaqHit.value ? 1 : 0;
        } finally {
          isLoading.value = false;
        }
      };

      // 'all'タブがアクティブになった時に検索を実行、URLとヘッダーのフォームを更新するウォッチャー
      watch(activeTab, (newTab) => {
        // サイト内全体検索の実行
        if (newTab === 'all') {
          allCurrentPage.value = 1;
          if (searchTerms.value) {
            performSearch();
          }
        }

        // URLのクエリパラメータを更新
        const url = new URL(window.location);
        url.searchParams.set('type', newTab);
        // 履歴エントリを追加せずにURLを置換する場合は replaceState を使用
        window.history.pushState({}, '', url);

        // ヘッダーの検索タイプ選択を更新
        const headerSearchSelect = document.getElementById('header-search-select');
        if (headerSearchSelect) {
          headerSearchSelect.value = newTab;
        }
      });

      onMounted(() => {
        const faqDataElement = document.getElementById('faq-data');
        if (faqDataElement) {
          faqData.value = JSON.parse(faqDataElement.textContent);
        }

        if (searchTerms.value && activeTab.value === 'all') {
          performSearch();
        }
      });

      watch(selectedFilters, () => {
        productsKey.value++;
        currentPage.value = 1;
      }, { deep: true });

      watch(inStockOnly, () => {
        productsKey.value++;
        currentPage.value = 1;
      });

      watch(sortOrder, () => {
        currentPage.value = 1;
      });

      // --- Product Search Logic ---
      const sortedProducts = computed(() => {
        if (!allProducts.value) return [];

        const normalizedQ = normalize(qParam.value);

        let filtered = allProducts.value.filter(product => {
          // In-stock filter
          if (inStockOnly.value && !product.available) {
            return false;
          }

          // Checkbox filters
          const passesCheckboxFilters = Object.keys(selectedFilters.value).every(filterKey => {
            const selected = selectedFilters.value[filterKey];
            if (selected.length === 0) return true;

            const productMetafieldValue = product.metafields ? product.metafields[filterKey] : null;

            if (Array.isArray(productMetafieldValue)) {
              // 配列内のいずれかの値が選択されたフィルタに含まれているかチェック
              return productMetafieldValue.some(value => selected.includes(value));
            }

            // 従来の単一オブジェクトの処理も残しておく
            let singleValue = productMetafieldValue;
            if (singleValue && typeof singleValue === 'object' && singleValue.name) {
              singleValue = singleValue.name;
            }

            return singleValue ? selected.includes(singleValue) : false;
          });

          if (!passesCheckboxFilters) return false;

          // Keyword search
          if (!normalizedQ) return true;

          if (normalize(product.title).includes(normalizedQ)) {
            return true;
          }

          const parent = product.metafields?.parent_product;
          if (parent) {
            const parentName = parent.product_name || '';
            const parentContent = extractTextFromRichText(parent.content);

            if (normalize(parentName).includes(normalizedQ) || normalize(parentContent).includes(normalizedQ)) {
              return true;
            }
          }

          return false;
        });

        // Sort
        switch (sortOrder.value) {
          case 'price-asc':
            filtered.sort((a, b) => a.price - b.price);
            break;
          case 'price-desc':
            filtered.sort((a, b) => b.price - a.price);
            break;
          case 'name-asc':
            filtered.sort((a, b) => a.title.localeCompare(b.title, 'ja'));
            break;
        }

        return filtered;
      });

      const totalPages = computed(() => {
        if (!sortedProducts.value) return 0;
        return Math.ceil(sortedProducts.value.length / perPage.value);
      });

      const paginatedProducts = computed(() => {
        if (!sortedProducts.value) return [];
        const start = (currentPage.value - 1) * perPage.value;
        const end = start + perPage.value;
        return sortedProducts.value.slice(start, end);
      });

      const changePage = (page) => {
        if (page >= 1 && page <= totalPages.value) {
          currentPage.value = page;
          const appElement = document.getElementById('app');
          if (appElement) {
            document.documentElement.style.scrollBehavior = 'auto';
            appElement.scrollIntoView({ behavior: 'auto' });
            setTimeout(() => {
              document.documentElement.style.scrollBehavior = '';
            }, 100);
          }
        }
      };

      // --- Formatting ---
      const formatCurrency = (price) => {
          if (price === null || typeof price === 'undefined') return '';
          // styleを'decimal'に変更して通貨記号を除外
          return new Intl.NumberFormat('ja-JP', { style: 'decimal' }).format(price / 100);
      };

      const formatFilterTitle = (key) => {
          if (!key) return '';
          const titles = {
            service: 'サービスで絞り込む',
            product_family: '商品から探す',
            category: 'カテゴリから探す',
            usage: '用途から探す'
          };
          return titles[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      };

      const stripBrackets = (text) => {
        return text.replace(/\[.*?\]/g, '').trim();
      }

      const resetFilters = () => {
        selectedFilters.value = {
          service: [],
          product_family: [],
          category: [],
          usage: [],
        };
        inStockOnly.value = false;
      };

      // ウィンドウのサイズ変更を監視
      const isPC = ref(window.innerWidth > 768);
      const checkWindowSize = () => {
        isPC.value = window.innerWidth > 768;
      };

      onMounted(() => {
        window.addEventListener('resize', checkWindowSize);
      });

      onUnmounted(() => {
        window.removeEventListener('resize', checkWindowSize);
      });

      return {
        activeTab,
        metaobjectFilters,
        selectedFilters,
        sortedProducts,
        formatCurrency,
        formatFilterTitle,
        searchTerms,
        searchResults,
        isLoading,
        performSearch,
        stripBrackets,
        productsKey,
        data,
        isPC,
        allSearchResultsCount,
        resetFilters,
        inStockOnly,
        sortOrder,
        isFaqHit,
        paginatedProducts,
        currentPage,
        totalPages,
        changePage,
        allCurrentPage,
        allTotalPages,
        changeAllPage,
        allPaginatedResults,
      };
    },
  });

  app.config.compilerOptions.delimiters = ['[[', ']]'];
  app.mount('#app');
};

/**
 * DOMの変更を監視し、#app要素が利用可能になったらVueを初期化する
 */
const observeAndInitVue = () => {
  const targetNode = document.body;
  const config = { childList: true, subtree: true };

  const callback = (mutationsList, observer) => {
    for (const mutation of mutationsList) {
      // ノードが追加された場合
      if (mutation.addedNodes.length > 0) {
        const appElement = document.getElementById('app');
        if (appElement && !appElement.__vue_app__) {
          // #app が追加され、まだVueがマウントされていなければ初期化
          initVueApp();
        }
      }
      // ノードが削除された場合
      if (mutation.removedNodes.length > 0) {
        // 削除されたノードの中に #app が含まれていたかチェック
        const wasAppRemoved = Array.from(mutation.removedNodes).some(node =>
          node.id === 'app' || (node.querySelector && node.querySelector('#app'))
        );
        if (wasAppRemoved) {
          // #app が削除された後、再度DOMに追加されたときに備えて再チェック
          const appElement = document.getElementById('app');
          if (appElement && !appElement.__vue_app__) {
            initVueApp();
          }
        }
      }
    }
  };

  const observer = new MutationObserver(callback);
  observer.observe(targetNode, config);

  // 初回読み込み時のために一度呼び出す
  initVueApp();
};

// ページが読み込まれたら監視を開始
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeAndInitVue);
} else {
  observeAndInitVue();
}
